import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articleModifiedMapAtom } from "@/src/state/articleModifiedMapAtom";
import { articlesAtom } from "@/src/state/articlesAtom";

import { FAQJson } from "@/src/components/molecules/FAQList/FAQList";

import {
  ARTICLES_VISIBILITY_SYNC_DEBOUNCE_MS,
  ARTICLE_INCREMENTAL_BODY_COUNT,
  ARTICLE_LIGHT_SYNC_MIN_INTERVAL_MS,
  ArticleListEntry,
  areArticleFilenamesEqual,
  findStaleFilenamesByModified,
  getHelsinkiWeekMondayKey,
  pickTopFilenames,
  readLastLightSyncAt,
  shouldRunWeeklyFullSync,
  sortArticleFilenamesDesc,
  writeLastFullSyncWeekId,
  writeLastLightSyncAt
} from "@/src/utils/articleSyncHelpers";

import { getArticleUrl } from "./slug";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import wretch from "wretch";

export { getArticleUrl };

export interface ArticleImage {
  kind: "ArticleImage";
  url: string;
  caption: string;
}

export interface ArticleSubheading {
  kind: "ArticleSubheading";
  value: string;
}

export interface ArticleBulletedList {
  kind: "ArticleBulletedList";
  itemList: string[];
}

export interface ArticleNumberedList {
  kind: "ArticleNumberedList";
  itemList: string[];
}

export interface ArticleLink {
  kind: "ArticleLink";
  url: string;
  text: string;
}

export interface ArticleParagraphTextItalic {
  kind: "ArticleParagraphTextItalic";
  value: string;
}

export interface ArticleTable {
  kind: "ArticleTable";
  columnHeaders: string[];
  rows: (string | number)[][];
}

export interface ArticleVideo {
  kind: "ArticleVideo";
  url: string;
  caption?: string;
}

/** Inline auction CTA in `body`; optional slot when merged from legacy fields. */
export interface ArticleCtaButton {
  kind: "ArticleCtaButton";
  text: string;
  /** Target URL from CMS; used to pick car / moto / caravan wizard routes. */
  href?: string;
  legacyGtmSlot?: "afterIngress" | "afterBody";
}

/** External link rendered as a prominent button in article body. */
export interface ArticleExternalButton {
  kind: "ArticleExternalButton";
  url: string;
  text: string;
}

interface ArticleParagraphText {
  kind: "ArticleParagraphText";
  value: string;
}

type ArticleParagraphContent = ArticleParagraphText | ArticleLink;

export type ArticleParagraph = {
  kind: "ArticleParagraph";
  contentItems: ArticleParagraphContent[];
};

export type ArticleBodyContent =
  | ArticleParagraph
  | ArticleImage
  | ArticleSubheading
  | ArticleBulletedList
  | ArticleNumberedList
  | ArticleLink
  | ArticleParagraphTextItalic
  | ArticleTable
  | ArticleVideo
  | ArticleCtaButton
  | ArticleExternalButton;

/** Optional CTA in article JSON: `{ "text": "...", "href": "..." }`. */
export interface ArticleCta {
  text: string;
  href: string;
}

export interface Article {
  title: string;
  ingress: string;
  date: string;
  mainImage: ArticleImage;
  body: ArticleBodyContent[];
  faqItemList?: number[];
  dateModified?: string;
  articleBody?: string;
  /** Shown below ingress when set (bucket JSON: `ctaAfterIngress`). */
  ctaAfterIngress?: ArticleCta;
  /** Shown after body, before FAQ when set (bucket JSON: `ctaAfterBody`). */
  ctaAfterBody?: ArticleCta;
}

const isLegacyArticleCtaActive = (
  cta: ArticleCta | undefined
): cta is ArticleCta =>
  cta != null && cta.text.trim() !== "" && cta.href.trim() !== "";

/**
 * Merges legacy top-level CTAs with `body` into one ordered list for render/SEO.
 */
export const normalizeArticleBody = (
  article: Article
): ArticleBodyContent[] => {
  const items: ArticleBodyContent[] = [];

  const ingressCta = article.ctaAfterIngress;
  if (isLegacyArticleCtaActive(ingressCta)) {
    items.push({
      kind: "ArticleCtaButton",
      text: ingressCta.text,
      href: ingressCta.href,
      legacyGtmSlot: "afterIngress"
    });
  }

  items.push(...(Array.isArray(article.body) ? article.body : []));

  const afterBodyCta = article.ctaAfterBody;
  if (isLegacyArticleCtaActive(afterBodyCta)) {
    items.push({
      kind: "ArticleCtaButton",
      text: afterBodyCta.text,
      href: afterBodyCta.href,
      legacyGtmSlot: "afterBody"
    });
  }

  return items;
};

const BUCKET_URL = import.meta.env.VITE_FRONTPAGE_BUCKET_URL;
const ARTICLES_PATH = import.meta.env.VITE_FRONTPAGE_ARTICLES_RELATIVE_PATH;
const ARTICLES_URL = `${BUCKET_URL}${ARTICLES_PATH}`;
const ARTICLE_LIST_FILENAME = import.meta.env
  .VITE_FRONTPAGE_ARTICLE_LIST_FILENAME;

export const listArticles = async (): Promise<unknown> => {
  const listArticlesUrl = `${BUCKET_URL}${ARTICLES_PATH}${ARTICLE_LIST_FILENAME}`;
  return wretch(listArticlesUrl, { cache: "no-store" }).get().json();
};

/**
 * Accepts both legacy (`string[]`) and enriched
 * (`Array<string | { filename, dateModified? }>`) manifest shapes and yields a
 * normalized list. Unknown entries are skipped to stay resilient.
 */
export const normalizeArticleListResponse = (
  raw: unknown
): ArticleListEntry[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const entries: ArticleListEntry[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      entries.push({ filename: item });
      continue;
    }
    if (item != null && typeof item === "object") {
      const candidate = item as {
        filename?: unknown;
        dateModified?: unknown;
      };
      if (typeof candidate.filename === "string") {
        entries.push({
          filename: candidate.filename,
          dateModified:
            typeof candidate.dateModified === "string"
              ? candidate.dateModified
              : undefined
        });
      }
    }
  }
  return entries;
};

const fetchArticleBodiesByFilenames = async (
  paths: string[]
): Promise<(Article | null)[]> => {
  if (paths.length === 0) {
    return [];
  }
  return Promise.all(
    paths.map((filename) =>
      wretch(`${ARTICLES_URL}${filename}`, { cache: "no-store" })
        .get()
        .json()
        .then((json) => json as Article)
        .catch((error) => {
          console.log(error);
          return null;
        })
    )
  );
};

const buildNextFilenameSlugMap = (
  filenames: string[],
  bodyFilenames: string[],
  results: (Article | null)[],
  prevMap: Record<string, string>
): Record<string, string> => {
  const newMap: Record<string, string> = {};

  for (const fn of filenames) {
    if (prevMap[fn]) {
      newMap[fn] = prevMap[fn];
    }
  }

  bodyFilenames.forEach((filename, i) => {
    const article = results[i];
    if (article != null) {
      newMap[filename] = getArticleUrl(article.title);
    }
  });

  return newMap;
};

const mergeArticlesForFilenames = (
  filenames: string[],
  nextFilenameToSlug: Record<string, string>,
  lookupBySlug: Record<string, Article>,
  prevArticles: Record<string, Article>
): Record<string, Article> => {
  const merged: Record<string, Article> = {};

  for (const fn of filenames) {
    const slug = nextFilenameToSlug[fn];
    if (!slug) continue;

    const body = lookupBySlug[slug] ?? prevArticles[slug];
    if (body != null) {
      merged[slug] = body;
    }
  }

  return merged;
};

let articlesSyncInFlight: Promise<void> | null = null;

const runArticlesSyncDeduped = (fn: () => Promise<void>): Promise<void> => {
  if (articlesSyncInFlight) {
    return articlesSyncInFlight;
  }
  articlesSyncInFlight = (async () => {
    try {
      await fn();
    } finally {
      articlesSyncInFlight = null;
    }
  })();
  return articlesSyncInFlight;
};

/**
 * Fetches the article manifest and normalizes it to `ArticleListEntry[]`,
 * returning `null` and logging on failure.
 */
const tryFetchArticleEntries = async (): Promise<ArticleListEntry[] | null> => {
  try {
    const raw = await listArticles();
    return normalizeArticleListResponse(raw);
  } catch (error) {
    console.log(error);
    return null;
  }
};

const entriesToFilenames = (entries: ArticleListEntry[]): string[] =>
  entries.map((e) => e.filename);

const buildModifiedMapFromEntries = (
  entries: ArticleListEntry[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const entry of entries) {
    if (entry.dateModified != null) {
      map[entry.filename] = entry.dateModified;
    }
  }
  return map;
};

const mergeModifiedMapForFetched = (
  prevMap: Record<string, string>,
  entries: ArticleListEntry[],
  fetchedFilenames: string[],
  results: (Article | null)[]
): Record<string, string> => {
  const allowedFilenames = new Set(entries.map((e) => e.filename));
  const next: Record<string, string> = {};
  for (const [filename, value] of Object.entries(prevMap)) {
    if (allowedFilenames.has(filename)) {
      next[filename] = value;
    }
  }
  const remoteByFilename = new Map(entries.map((e) => [e.filename, e]));
  fetchedFilenames.forEach((filename, i) => {
    const fetched = results[i];
    if (fetched == null) {
      return;
    }
    const remote = remoteByFilename.get(filename);
    if (remote?.dateModified != null) {
      next[filename] = remote.dateModified;
    }
  });
  return next;
};

/** Builds a slug → Article lookup from parallel filename/result arrays. */
const buildSlugLookup = (
  filenames: string[],
  results: (Article | null)[]
): Record<string, Article> => {
  const lookup: Record<string, Article> = {};
  filenames.forEach((filename, i) => {
    const article = results[i];
    if (article != null) {
      lookup[getArticleUrl(article.title)] = article;
    }
  });
  return lookup;
};

type BodySyncMode = { bodyFilenames: string[]; isFullBodySync: boolean };

interface ResolveBodySyncModeParams {
  hasCachedArticles: boolean;
  needsFilenameSlugBackfill: boolean;
  forceFullFromListChange: boolean;
  filenames: string[];
  sorted: string[];
  staleFilenames: string[];
  pathname: string;
  now: Date;
}

const uniqueFilenames = (filenames: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const filename of filenames) {
    if (!seen.has(filename)) {
      seen.add(filename);
      result.push(filename);
    }
  }
  return result;
};

/**
 * Determines which article bodies need to be fetched and whether this is a
 * full or incremental sync. In incremental mode, also includes any filenames
 * whose remote `dateModified` differs from the locally stored value.
 */
const resolveBodySyncMode = ({
  hasCachedArticles,
  needsFilenameSlugBackfill,
  forceFullFromListChange,
  filenames,
  sorted,
  staleFilenames,
  pathname,
  now
}: ResolveBodySyncModeParams): BodySyncMode => {
  const isWeeklyFull = shouldRunWeeklyFullSync({
    pathname,
    hasCachedArticles: true,
    now
  });

  const needsFullSync =
    !hasCachedArticles ||
    isWeeklyFull ||
    needsFilenameSlugBackfill ||
    forceFullFromListChange;

  if (needsFullSync) {
    return { bodyFilenames: filenames, isFullBodySync: true };
  }

  const filenameSet = new Set(filenames);
  const stale = staleFilenames.filter((fn) => filenameSet.has(fn));

  return {
    bodyFilenames: uniqueFilenames([
      ...pickTopFilenames(sorted, ARTICLE_INCREMENTAL_BODY_COUNT),
      ...stale
    ]),
    isFullBodySync: false
  };
};

type CachedPrefetchOutcome =
  | { kind: "abort" }
  | {
      kind: "ok";
      prefetchedEntries: ArticleListEntry[] | null;
      staleFilenames: string[];
      forceFullFromListChange: boolean;
    };

const runCachedArticlePrefetchPhase = async (params: {
  pathname: string;
  now: Date;
  articleList: string[];
  filenameToSlug: Record<string, string>;
  modifiedMap: Record<string, string>;
}): Promise<CachedPrefetchOutcome> => {
  const isWeeklyFull = shouldRunWeeklyFullSync({
    pathname: params.pathname,
    hasCachedArticles: true,
    now: params.now
  });

  if (isWeeklyFull) {
    return {
      kind: "ok",
      prefetchedEntries: null,
      staleFilenames: [],
      forceFullFromListChange: false
    };
  }

  const prefetchedEntries = await tryFetchArticleEntries();
  if (!Array.isArray(prefetchedEntries)) {
    return { kind: "abort" };
  }

  const prefetchedFilenames = entriesToFilenames(prefetchedEntries);
  const listUnchanged = areArticleFilenamesEqual(
    prefetchedFilenames,
    params.articleList
  );

  const staleFilenames = findStaleFilenamesByModified(
    prefetchedEntries,
    params.modifiedMap
  );

  if (listUnchanged) {
    const filenameSlugMapEmpty =
      Object.keys(params.filenameToSlug).length === 0;
    const lastLight = readLastLightSyncAt();
    const throttled =
      !filenameSlugMapEmpty &&
      lastLight != null &&
      Date.now() - lastLight < ARTICLE_LIGHT_SYNC_MIN_INTERVAL_MS;

    if (throttled && staleFilenames.length === 0) {
      return { kind: "abort" };
    }
    return {
      kind: "ok",
      prefetchedEntries,
      staleFilenames,
      forceFullFromListChange: false
    };
  }

  return {
    kind: "ok",
    prefetchedEntries,
    staleFilenames,
    forceFullFromListChange: true
  };
};

const applySyncedArticleBodies = async (params: {
  prefetchedEntries: ArticleListEntry[] | null;
  staleFilenames: string[];
  hasCachedArticles: boolean;
  forceFullFromListChange: boolean;
  pathname: string;
  now: Date;
  articlesRef: MutableRefObject<Record<string, Article>>;
  filenameToSlugRef: MutableRefObject<Record<string, string>>;
  modifiedMapRef: MutableRefObject<Record<string, string>>;
  setArticleList: (filenames: string[]) => void;
  setFilenameToSlug: (map: Record<string, string>) => void;
  setArticles: (articles: Record<string, Article>) => void;
  setModifiedMap: (map: Record<string, string>) => void;
}): Promise<boolean> => {
  const entries = params.prefetchedEntries ?? (await tryFetchArticleEntries());
  if (!Array.isArray(entries)) {
    return false;
  }

  const filenames = entriesToFilenames(entries);
  params.setArticleList(filenames);

  const sorted = sortArticleFilenamesDesc(filenames);
  const needsFilenameSlugBackfill =
    params.hasCachedArticles &&
    filenames.length > 0 &&
    Object.keys(params.filenameToSlugRef.current).length === 0;

  // When we re-fetch ourselves (full sync path) the prefetch phase ran
  // earlier and already gave us the up-to-date `staleFilenames` from the same
  // entries; if not, recompute against what we just normalized.
  const staleFilenames =
    params.prefetchedEntries != null
      ? params.staleFilenames
      : findStaleFilenamesByModified(entries, params.modifiedMapRef.current);

  const { bodyFilenames, isFullBodySync } = resolveBodySyncMode({
    hasCachedArticles: params.hasCachedArticles,
    needsFilenameSlugBackfill,
    forceFullFromListChange: params.forceFullFromListChange,
    filenames,
    sorted,
    staleFilenames,
    pathname: params.pathname,
    now: params.now
  });

  if (bodyFilenames.length === 0) {
    return false;
  }

  const results = await fetchArticleBodiesByFilenames(bodyFilenames);
  const anyFetched = results.some((a) => a != null);
  if (!anyFetched && !params.hasCachedArticles) {
    return false;
  }

  const lookupBySlug = buildSlugLookup(bodyFilenames, results);

  const nextFilenameToSlug = buildNextFilenameSlugMap(
    filenames,
    bodyFilenames,
    results,
    params.filenameToSlugRef.current
  );

  const mergedArticles = mergeArticlesForFilenames(
    filenames,
    nextFilenameToSlug,
    lookupBySlug,
    params.articlesRef.current
  );

  const mergedCount = Object.keys(mergedArticles).length;
  const cachedArticleCount = Object.keys(params.articlesRef.current).length;
  const shouldApplyArticles = mergedCount > 0 || cachedArticleCount === 0;

  if (shouldApplyArticles) {
    params.setArticles(mergedArticles);
  }

  if (Object.keys(nextFilenameToSlug).length > 0) {
    params.setFilenameToSlug(nextFilenameToSlug);
  }

  const nextModifiedMap = isFullBodySync
    ? // Full sync rebuilds the map directly from the remote manifest; older
      // filenames that have been removed naturally drop out.
      buildModifiedMapFromEntries(entries)
    : mergeModifiedMapForFetched(
        params.modifiedMapRef.current,
        entries,
        bodyFilenames,
        results
      );
  params.setModifiedMap(nextModifiedMap);

  if (isFullBodySync && !needsFilenameSlugBackfill) {
    writeLastFullSyncWeekId(getHelsinkiWeekMondayKey(params.now));
  }
  writeLastLightSyncAt(Date.now());
  return true;
};

export const useGetArticles = () => {
  const [isLoading, setIsLoading] = useState(false);
  const setArticleList = useSetAtom(articleListAtom);
  const articleList = useAtomValue(articleListAtom);
  const [articles, setArticles] = useAtom(articlesAtom);
  const [filenameToSlug, setFilenameToSlug] = useAtom(
    articleFilenameToSlugAtom
  );
  const [modifiedMap, setModifiedMap] = useAtom(articleModifiedMapAtom);
  const location = useLocation();
  const articlesRef = useRef(articles);
  articlesRef.current = articles;
  const articleListRef = useRef(articleList);
  articleListRef.current = articleList;
  const filenameToSlugRef = useRef(filenameToSlug);
  filenameToSlugRef.current = filenameToSlug;
  const modifiedMapRef = useRef(modifiedMap);
  modifiedMapRef.current = modifiedMap;
  const lastVisibilitySyncRef = useRef(0);

  const runSync = useCallback(async () => {
    await runArticlesSyncDeduped(async () => {
      const hasCachedArticles = Object.keys(articlesRef.current).length > 0;
      const showGlobalLoading = !hasCachedArticles;
      const now = new Date();
      let prefetchedEntries: ArticleListEntry[] | null = null;
      let staleFilenames: string[] = [];
      let forceFullFromListChange = false;

      if (hasCachedArticles) {
        const prefetchOutcome = await runCachedArticlePrefetchPhase({
          pathname: location.pathname,
          now,
          articleList: articleListRef.current,
          filenameToSlug: filenameToSlugRef.current,
          modifiedMap: modifiedMapRef.current
        });
        if (prefetchOutcome.kind === "abort") {
          return;
        }
        prefetchedEntries = prefetchOutcome.prefetchedEntries;
        staleFilenames = prefetchOutcome.staleFilenames;
        forceFullFromListChange = prefetchOutcome.forceFullFromListChange;
      }

      if (showGlobalLoading) setIsLoading(true);

      try {
        await applySyncedArticleBodies({
          prefetchedEntries,
          staleFilenames,
          hasCachedArticles,
          forceFullFromListChange,
          pathname: location.pathname,
          now,
          articlesRef,
          filenameToSlugRef,
          modifiedMapRef,
          setArticleList,
          setFilenameToSlug,
          setArticles,
          setModifiedMap
        });
      } finally {
        if (showGlobalLoading) setIsLoading(false);
      }
    });
  }, [
    location.pathname,
    setArticleList,
    setArticles,
    setFilenameToSlug,
    setModifiedMap
  ]);

  useEffect(() => {
    runSync().catch((error) => {
      console.log(error);
    });
  }, [runSync]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const t = Date.now();
      if (
        t - lastVisibilitySyncRef.current <
        ARTICLES_VISIBILITY_SYNC_DEBOUNCE_MS
      ) {
        return;
      }
      lastVisibilitySyncRef.current = t;
      runSync().catch((error) => {
        console.log(error);
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [runSync]);

  return { articles, isLoading };
};

export const formatArticleDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fi-FI");
};

/**
 * Converts a date string (e.g., "2025-11-05") to ISO 8601 datetime format
 * with correct Europe/Helsinki timezone offset (e.g., "2025-11-05T00:00:00+02:00"
 * for winter, "2025-06-15T00:00:00+03:00" for summer) for structured data purposes.
 * Handles DST correctly: +02:00 in winter, +03:00 in summer.
 * Uses native JavaScript Intl API to avoid external dependencies.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO 8601 datetime string with 00:00:00 time and correct Helsinki timezone offset
 */
export const convertDateToIsoDatetime = (dateString: string): string => {
  const HELSINKI_TIMEZONE = "Europe/Helsinki";

  // Validate input format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new Error(
      `Invalid date format. Expected YYYY-MM-DD, got: ${dateString}`
    );
  }

  // Parse the date string components
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a date at noon UTC (to avoid DST transition edge cases)
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

  // Format this UTC date in Helsinki timezone to see what time it is there
  const helsinkiNoon = utcNoon.toLocaleString("en-US", {
    timeZone: HELSINKI_TIMEZONE,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit"
  });

  // Parse Helsinki time (format: "HH:MM")
  const [helsinkiHour] = helsinkiNoon.split(":").map(Number);

  // Calculate offset: Helsinki is UTC+2 (winter) or UTC+3 (summer)
  // At noon UTC, Helsinki shows 14:00 (winter) or 15:00 (summer)
  const offsetHours = helsinkiHour - 12;

  // Format offset string (+02:00 or +03:00)
  const offsetString = `+${offsetHours.toString().padStart(2, "0")}:00`;

  // Return ISO format with Helsinki timezone offset at midnight
  return `${dateString}T00:00:00${offsetString}`;
};

export const btoaUTF8 = (str: string): string =>
  btoa(
    encodeURIComponent(str).replaceAll(/%([0-9A-F]{2})/g, (_match, p1) =>
      String.fromCodePoint(Number.parseInt(p1, 16))
    )
  );

export const faqItemsExist = (ids: number[]): boolean =>
  FAQJson.data.some((item) => ids.includes(item.id));

/**
 * Ensures text ends with punctuation (. ! ?) to avoid duplicate periods
 * @param text - Text to ensure punctuation for
 * @returns Text with punctuation at the end
 */
const ensurePunctuation = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const endsWithPunctuation = /[.!?]$/.test(trimmed);
  return endsWithPunctuation ? trimmed : `${trimmed}.`;
};

/**
 * Combines article ingress and body content into a single text string
 * for SEO structured data and Google crawler purposes.
 * @param article - The article object containing ingress and body content
 * @returns Combined text string with all article text content, or empty string if no content
 */
export const combineArticleBodyText = (article: Article): string => {
  const textParts: string[] = [];

  // Start with ingress
  if (article.ingress && article.ingress.trim()) {
    textParts.push(ensurePunctuation(article.ingress));
  }

  // Process each body content item (including legacy CTAs merged in order)
  const bodyItems = normalizeArticleBody(article);
  bodyItems.forEach((content) => {
    switch (content.kind) {
      case "ArticleSubheading":
        textParts.push(ensurePunctuation(content.value));
        break;

      case "ArticleParagraph":
        content.contentItems.forEach((item) => {
          if (item.kind === "ArticleParagraphText") {
            textParts.push(ensurePunctuation(item.value));
          } else if (item.kind === "ArticleLink") {
            // Include link text for SEO purposes
            textParts.push(ensurePunctuation(item.text));
          }
        });
        break;

      case "ArticleBulletedList":
        content.itemList.forEach((item) => {
          textParts.push(ensurePunctuation(item));
        });
        break;

      case "ArticleNumberedList":
        content.itemList.forEach((item) => {
          textParts.push(ensurePunctuation(item));
        });
        break;

      case "ArticleParagraphTextItalic":
        textParts.push(ensurePunctuation(content.value));
        break;

      case "ArticleLink":
        // Include link text for SEO purposes
        textParts.push(ensurePunctuation(content.text));
        break;

      case "ArticleImage":
        // Include image caption for SEO purposes
        if (content.caption) {
          textParts.push(ensurePunctuation(content.caption));
        }
        break;

      case "ArticleVideo":
        // Include video caption for SEO purposes
        if (content.caption) {
          textParts.push(ensurePunctuation(content.caption));
        }
        break;

      case "ArticleTable":
        // Include column headers for context
        content.columnHeaders.forEach((header) => {
          textParts.push(ensurePunctuation(header));
        });
        // Include all cell values from rows
        content.rows.forEach((row) => {
          row.forEach((cell) => {
            // Convert cell to string (handles numbers and other types)
            const cellString = String(cell);
            textParts.push(ensurePunctuation(cellString));
          });
        });
        break;

      case "ArticleCtaButton":
        if (content.text.trim()) {
          textParts.push(ensurePunctuation(content.text));
        }
        break;

      case "ArticleExternalButton":
        if (content.text.trim()) {
          textParts.push(ensurePunctuation(content.text));
        }
        break;

      default:
        break;
    }
  });

  // Join all parts with spaces and normalize whitespace
  // Replace multiple spaces with single space, trim edges
  const combined = textParts.join(" ").replace(/\s+/g, " ").trim();

  // Return empty string if no content (better than undefined for SEO)
  return combined || "";
};
